import { Inject, Injectable, ViewContainerRef } from '@angular/core';

import { I18nService } from '@recruitee/i18n';
import { DialogRef, DialogService, DialogWidth } from '@recruitee/user-interface';

import { TutorialDialogModuleTranslations, TutorialTranslations } from './translations';
import { TutorialDialogComponent } from './tutorial-dialog.component';
import { AssetsProvider, TutorialDialogContext, TutorialStep } from './tutorial-dialog.types';

@Injectable({ providedIn: 'root' })
export class TutorialDialogService {
  public readonly VIDEO_CALLING_CONFIG: TutorialStep[] = [
    {
      imgUrl: this.assetsProvider('video-calling/video-calling-step-1.png'),
      imgUrl2x: this.assetsProvider('video-calling/video-calling-step-1_2x.png'),
      title:
        this.translations.tutorials.video_calling &&
        this.i18n.translate(this.translations.tutorials.video_calling.step_1.title),
      description:
        this.translations.tutorials.video_calling &&
        this.i18n.translate(this.translations.tutorials.video_calling.step_1.description),
    },
    {
      imgUrl: this.assetsProvider('video-calling/video-calling-step-2.png'),
      imgUrl2x: this.assetsProvider('video-calling/video-calling-step-2_2x.png'),
      title:
        this.translations.tutorials.video_calling &&
        this.i18n.translate(this.translations.tutorials.video_calling.step_2.title),
      description:
        this.translations.tutorials.video_calling &&
        this.i18n.translate(this.translations.tutorials.video_calling.step_2.description),
    },
    {
      imgUrl: this.assetsProvider('video-calling/video-calling-step-3.png'),
      imgUrl2x: this.assetsProvider('video-calling/video-calling-step-3_2x.png'),
      title:
        this.translations.tutorials.video_calling &&
        this.i18n.translate(this.translations.tutorials.video_calling.step_3.title),
      description:
        this.translations.tutorials.video_calling &&
        this.i18n.translate(this.translations.tutorials.video_calling.step_3.description),
      link: 'https://support.recruitee.com/recruitee-s-key-features-explained/using-video/get-started-with-video-calls-beta',
    },
  ];

  public readonly MULTILANG_INTRO_CONFIG: TutorialStep[] = [
    {
      imgUrl: this.assetsProvider('multilang-intro/multilang-intro-step-1.png'),
      imgUrl2x: this.assetsProvider('multilang-intro/multilang-intro-step-1_2x.png'),
      title:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_1.title),
      description:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_1.description),
    },
    {
      imgUrl: this.assetsProvider('multilang-intro/multilang-intro-step-2.png'),
      imgUrl2x: this.assetsProvider('multilang-intro/multilang-intro-step-2_2x.png'),
      title:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_2.title),
      description:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_2.description),
    },
    {
      imgUrl: this.assetsProvider('multilang-intro/multilang-intro-step-3.png'),
      imgUrl2x: this.assetsProvider('multilang-intro/multilang-intro-step-3_2x.png'),
      title:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_3.title),
      description:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_3.description),
    },
  ];

  public readonly MULTILANG_OFFER_CONFIG: TutorialStep[] = [
    {
      imgUrl: this.assetsProvider('multilang-offer/multilang-offer-step-1.png'),
      imgUrl2x: this.assetsProvider('multilang-offer/multilang-offer-step-1_2x.png'),
      title:
        this.translations.tutorials.multilang_offer &&
        this.i18n.translate(this.translations.tutorials.multilang_offer.step_1.title),
      description:
        this.translations.tutorials.multilang_offer &&
        this.i18n.translate(this.translations.tutorials.multilang_offer.step_1.description),
    },
    {
      imgUrl: this.assetsProvider('multilang-offer/multilang-offer-step-2.png'),
      imgUrl2x: this.assetsProvider('multilang-offer/multilang-offer-step-2_2x.png'),
      title:
        this.translations.tutorials.multilang_offer &&
        this.i18n.translate(this.translations.tutorials.multilang_offer.step_2.title),
      description:
        this.translations.tutorials.multilang_offer &&
        this.i18n.translate(this.translations.tutorials.multilang_offer.step_2.description),
    },
    {
      imgUrl: this.assetsProvider('multilang-offer/multilang-offer-step-3.png'),
      imgUrl2x: this.assetsProvider('multilang-offer/multilang-offer-step-3_2x.png'),
      title:
        this.translations.tutorials.multilang_offer &&
        this.i18n.translate(this.translations.tutorials.multilang_offer.step_3.title),
      description:
        this.translations.tutorials.multilang_offer &&
        this.i18n.translate(this.translations.tutorials.multilang_offer.step_3.description),
    },
  ];

  public readonly REFERRALS_ONBOARDING_CONFIG: TutorialStep[] = [
    {
      imgUrl: this.assetsProvider('referrals-onboarding/referrals-onboarding-step-0.svg'),
      title:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_0.title),
      description:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_0.description),
      isStep0: true,
    },
    {
      imgUrl: this.assetsProvider('referrals-onboarding/referrals-onboarding-step-2.svg'),
      title:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_2.title),
      description:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_2.description),
    },
    {
      imgUrl: this.assetsProvider('referrals-onboarding/referrals-onboarding-step-3.svg'),
      title:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_3.title),
      description:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_3.description),
    },
    {
      imgUrl: this.assetsProvider('referrals-onboarding/referrals-onboarding-step-4.svg'),
      title:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_4.title),
      description:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_4.description),
    },
  ];

  public readonly REQUISITIONS_INTRO_CONFIG: TutorialStep[] = [
    {
      youtubeVideoId: 'eZ5QRKliX-c', // TEMP
      title:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_1.title),
      description:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_1.description),
    },
    {
      imgUrl: this.assetsProvider('requisitions-intro/requisitions-intro-step-2.svg'),
      title:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_2.title),
      description:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_2.description),
    },
    {
      imgUrl: this.assetsProvider('requisitions-intro/requisitions-intro-step-3.svg'),
      title:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_3.title),
      description:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_3.description),
    },
    {
      imgUrl: this.assetsProvider('requisitions-intro/requisitions-intro-step-4.svg'),
      title:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_4.title),
      description:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_4.description),
      link:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_4.link),
    },
  ];

  public readonly REQUISITIONS_JOB_CONFIG: TutorialStep[] = [
    {
      imgUrl: this.assetsProvider('requisitions-job/requisitions-job-step-1.svg'),
      title:
        this.translations.tutorials.requisitions_job &&
        this.i18n.translate(this.translations.tutorials.requisitions_job.step_1.title),
      description:
        this.translations.tutorials.requisitions_job &&
        this.i18n.translate(this.translations.tutorials.requisitions_job.step_1.description),
    },
    {
      imgUrl: this.assetsProvider('requisitions-job/requisitions-job-step-2.svg'),
      title:
        this.translations.tutorials.requisitions_job &&
        this.i18n.translate(this.translations.tutorials.requisitions_job.step_2.title),
      description:
        this.translations.tutorials.requisitions_job &&
        this.i18n.translate(this.translations.tutorials.requisitions_job.step_2.description),
      link:
        this.translations.tutorials.requisitions_job &&
        this.i18n.translate(this.translations.tutorials.requisitions_job.step_2.link),
    },
  ];

  public tutorials: { [key in TutorialDialogContext]: TutorialStep[] } = {
    video_calling: this.VIDEO_CALLING_CONFIG,
    multilang_intro: this.MULTILANG_INTRO_CONFIG,
    multilang_offer: this.MULTILANG_OFFER_CONFIG,
    referrals_onboarding: this.REFERRALS_ONBOARDING_CONFIG,
    requisitions_intro: this.REQUISITIONS_INTRO_CONFIG,
    requisitions_job: this.REQUISITIONS_JOB_CONFIG,
  };

  private analyticsContextMap: { [key in TutorialDialogContext]: string } = {
    video_calling: '[ScheduleFirstStep]',
    multilang_intro: '[Settings/Langauges]',
    multilang_offer: '[OfferForm]',
    referrals_onboarding: '[ReferralsOnboarding]',
    requisitions_intro: '[Requisition]',
    requisitions_job: '[Offer]',
  };

  constructor(
    private dialogService: DialogService,
    private i18n: I18nService,
    @Inject(TutorialTranslations)
    public translations: TutorialDialogModuleTranslations,
    @Inject(AssetsProvider) private assetsProvider: (filePath: string) => string,
  ) {
    this.bindDialogGlobalTrigger();
  }

  public open(context: TutorialDialogContext, vcRef: ViewContainerRef): DialogRef {
    return this.dialogService.custom(
      TutorialDialogComponent,
      { width: DialogWidth.Small, showCloseButton: false },
      { config: this.tutorials[context], analyticsContext: this.analyticsContextMap[context] },
      vcRef,
    );
  }

  private bindDialogGlobalTrigger(): void {
    // @ts-ignore
    window['tutorialDialog'] = (context: TutorialDialogContext) => {
      this.open(context, <any>null);
    };
  }
}
